/**
 * orderDisplay.js
 * ─────────────────────────────────────────────────────────────────────────────
 * How an order describes itself to the customer. One module, because the same
 * four bugs appeared on every screen that rendered order state.
 *
 * WHAT WENT WRONG (July-2026 order-display audit, from real screenshots)
 * ----------------------------------------------------------------------
 * 1. RAW ENUMS ON SCREEN. `Order {order.delivery_status}` printed
 *    "Order NOT_APPLICABLE" to a customer who had bought a cinema ticket.
 *    NOT_APPLICABLE is a correct DATABASE value — a ticket has no delivery leg —
 *    but it is not a sentence.
 *
 * 2. EVERY ORDER CLAIMED TO BE WITH A RESTAURANT. `vendorFulfillment.status`
 *    defaults to "PENDING" on the Orders schema, for all orders of every kind.
 *    The banner keyed straight off it, so a movie ticket, a grocery order and a
 *    pharmacy order all announced "Sent to the restaurant — awaiting
 *    confirmation". `hasRestaurantFulfillment` exists to tell an UNTOUCHED
 *    default apart from a real restaurant order awaiting a real decision.
 *
 * 3. THE SCREEN CONTRADICTED ITSELF. Because that banner was independent of
 *    delivery state, a DELIVERED order displayed a green "Order DELIVERED"
 *    button AND "Sent to the restaurant — awaiting confirmation" at the same
 *    time. Delivery state is downstream of fulfilment: once it is terminal, the
 *    fulfilment question is settled by definition.
 *
 * 4. THE STEPPER WAS OFF BY ONE. Labels are
 *    [Cart, Processing, Dispatched, Delivered, Cancelled] — indices 0-4 — but
 *    DELIVERED set position 4, which is "5. Order Cancelled". A delivered order
 *    lit up the cancelled step. REJECTED set 5, past the end of the array.
 *
 * Byte-identical in Lusuku_Expo_Clean and Lusuku_IOS — keep it that way.
 */

export const STEP_LABELS = [
  "1. Cart Selection",
  "2. Order Processing",
  "3. Order dispatched",
  "4. Order Delivered",
  "5. Order Cancelled",
];

/** A ticket has no delivery leg — no rider, no address, no stepper. */
export function isTicketOrder(order) {
  return order?.orderKind === "TICKET";
}

/**
 * Is the restaurant accept/prepare flow ACTUALLY in play for this order?
 *
 * `vendorFulfillment.status` cannot answer this on its own: the schema defaults
 * it to "PENDING", so an untouched sub-document on a grocery order is
 * indistinguishable from a real restaurant order genuinely awaiting
 * confirmation. Anything OTHER than the default — a decision, a timestamp, a
 * prep time — proves the flow was actually entered.
 */
export function hasRestaurantFulfillment(order) {
  const vf = order?.vendorFulfillment;
  if (!vf) return false;
  if (vf.status && vf.status !== "PENDING") return true; // acted on
  return Boolean(
    vf.acceptedAt ||
      vf.readyAt ||
      vf.rejectedAt ||
      vf.promisedReadyAt ||
      Number(vf.prepTimeMins) > 0 ||
      Number(vf.prepTimeAdjustedMins) > 0
  );
}

/** Delivery state that is settled — nothing upstream is still "awaiting". */
const TERMINAL_DELIVERY = new Set([
  "DELIVERED",
  "REJECTED",
  "FULFILLED",
  "NOT_APPLICABLE",
]);

export function isDeliveryTerminal(order) {
  return TERMINAL_DELIVERY.has(order?.delivery_status);
}

/**
 * Should the restaurant banner be shown at all?
 *
 * Both conditions matter. The first stops a movie ticket claiming a restaurant;
 * the second stops a delivered order still "awaiting confirmation".
 */
export function shouldShowFulfillmentBanner(order) {
  if (isTicketOrder(order)) return false;
  if (isDeliveryTerminal(order)) return false;
  return hasRestaurantFulfillment(order);
}

/** A physical order has a delivery journey; a ticket does not. */
export function shouldShowDeliveryStepper(order) {
  if (isTicketOrder(order)) return false;
  return order?.delivery_status !== "NOT_APPLICABLE";
}

/**
 * Index into STEP_LABELS. Previously off by one in both directions.
 * @returns {number} 0-4, always inside the array
 */
export function stepperIndex(order) {
  switch (order?.delivery_status) {
    case "PROCESSING":
    case "PENDING_3PL":
    case "ASSIGNED":
    case "ACCEPTED":
      return 1; // "2. Order Processing"
    case "PICKED_UP":
    case "DISPATCHED":
    case "IN_TRANSIT":
    case "ARRIVED":
      return 2; // "3. Order dispatched"
    case "DELIVERED":
    case "FULFILLED":
      return 3; // "4. Order Delivered"  ← was 4 = "5. Order Cancelled"
    case "REJECTED":
      return 4; // "5. Order Cancelled"  ← was 5, past the end of the array
    default:
      return 0; // "1. Cart Selection"
  }
}

/**
 * The sentence a customer reads. NEVER the raw enum.
 *
 * Ticket orders get ticket language: they were never going to be "dispatched",
 * and NOT_APPLICABLE is a statement about our delivery system, not about their
 * purchase.
 */
export function deliveryLabel(order) {
  if (isTicketOrder(order)) {
    if (order?.isPaid) return "Ticket issued";
    return "Awaiting payment";
  }

  switch (order?.delivery_status) {
    case "PROCESSING":
      return "Processing";
    case "PENDING_3PL":
      return "Finding a rider";
    case "ASSIGNED":
    case "ACCEPTED":
      return "Rider assigned";
    case "PICKED_UP":
    case "DISPATCHED":
      return "On the way";
    case "IN_TRANSIT":
      return "In transit";
    case "ARRIVED":
      return "Rider has arrived";
    case "DELIVERED":
      return "Delivered";
    case "FULFILLED":
      return "Completed";
    case "REJECTED":
      return "Cancelled";
    case "NOT_APPLICABLE":
      // No delivery leg. Say what IS true rather than naming the absence.
      return order?.isPaid ? "Completed" : "Awaiting payment";
    default:
      return "Processing";
  }
}

/** Semantic colour for the status pill/button. */
export function deliveryTone(order) {
  if (isTicketOrder(order)) return order?.isPaid ? "success" : "pending";
  switch (order?.delivery_status) {
    case "DELIVERED":
    case "FULFILLED":
      return "success";
    case "REJECTED":
      return "danger";
    case "NOT_APPLICABLE":
      return order?.isPaid ? "success" : "pending";
    default:
      return "pending";
  }
}

// Named before export — CRA's `import/no-anonymous-default-export` warning
// becomes a hard error on Netlify (CI=true treats warnings as errors), so the
// default export must be assigned to a named variable first.
const orderDisplay = {
  STEP_LABELS,
  isTicketOrder,
  hasRestaurantFulfillment,
  isDeliveryTerminal,
  shouldShowFulfillmentBanner,
  shouldShowDeliveryStepper,
  stepperIndex,
  deliveryLabel,
  deliveryTone,
};

export default orderDisplay;
